import { Component, forwardRef, Input, OnChanges, SecurityContext, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';

@Component({
    selector: 'app-rich-text-editor',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, AngularEditorModule],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RichTextEditorComponent),
            multi: true
        }
    ],
    template: `
        <div *ngIf="title" class="section-header">{{ title }}</div>
        <div class="rte-wrapper" [class.rte-disabled]="disabled" [class.rte-focused]="isFocused">
            <angular-editor
                [id]="editorId"
                [formControl]="textEditorFormGroup.controls.htmlEditorControl"
                [config]="editorConfig"
                [placeholder]="placeholder"
                (focus)="isFocused = true"
                (blur)="isFocused = false; onTouched()"
            ></angular-editor>
        </div>
        <div *ngIf="maxLength" class="d-flex justify-content-end">
            <span [style.color]="characterCount > maxLength ? 'red' : 'inherit'">
                {{ characterCount }} / {{ maxLength }}
            </span>
        </div>
    `,
    styles: [`
        app-rich-text-editor {
            display: block;
            width: 100%;
            min-width: 0;
        }
        app-rich-text-editor .rte-wrapper {
            border: 1px solid rgba(0,0,0,0.38);
            border-radius: 4px;
            background: #fff;
            transition: border-color 0.2s, box-shadow 0.2s;
            width: 100%;
        }
        app-rich-text-editor .rte-wrapper.rte-focused {
            border-color: #1976d2;
            box-shadow: 0 0 0 1px #1976d2;
        }
        app-rich-text-editor .rte-wrapper.rte-disabled {
            opacity: 0.6;
            pointer-events: none;
            background: #f5f5f5;
        }
        app-rich-text-editor:not(.configuration-editor) .angular-editor-textarea {
            border: none !important;
            margin-top: 0 !important;
            font-size: 14px;
            line-height: 1.5;
            color: rgba(0,0,0,0.87);
        }
        app-rich-text-editor.configuration-editor .rte-wrapper {
            border: none;
            border-radius: 0;
            box-shadow: none;
            background: transparent;
        }
        app-rich-text-editor.workbench-note-editor .angular-editor .angular-editor-wrapper .angular-editor-textarea {
            background-color: #fff !important;
        }
        app-rich-text-editor.sidebar-note-editor .rte-wrapper {
            overflow: hidden;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor {
            padding-top: 0 !important;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 4px;
            padding: 4px 6px;
            border: none;
            border-bottom: 1px solid #ddd;
            background: #f5f5f5;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor-toolbar-set {
            margin: 0 !important;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor-toolbar-set:has(button:not([hidden]), ae-select:not([hidden])) {
            display: flex !important;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor-toolbar-set:not(:has(button:not([hidden]), ae-select:not([hidden]))) {
            display: none !important;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor .angular-editor-toolbar .angular-editor-toolbar-set .angular-editor-button {
            float: none;
            width: 28px;
            min-width: 28px !important;
            height: 28px;
            padding: 4px;
            border: none !important;
            border-radius: 3px;
            background: transparent !important;
            color: rgba(0,0,0,0.87);
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor .angular-editor-toolbar .angular-editor-toolbar-set .angular-editor-button:hover,
        app-rich-text-editor.sidebar-note-editor .angular-editor .angular-editor-toolbar .angular-editor-toolbar-set .angular-editor-button:focus-visible {
            background: #e0e0e0 !important;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor .angular-editor-toolbar .angular-editor-toolbar-set .angular-editor-button.active {
            background: #2c6eed !important;
            color: #fff;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor .angular-editor-toolbar .angular-editor-toolbar-set .angular-editor-button:disabled {
            background: transparent !important;
            color: #aaa;
        }
        app-rich-text-editor.sidebar-note-editor .angular-editor-textarea {
            padding: 10px 12px !important;
            resize: none;
        }
    `]
})
export class RichTextEditorComponent implements ControlValueAccessor, OnChanges {
    private static nextEditorId = 0;
    readonly editorId = `rich-text-editor-${RichTextEditorComponent.nextEditorId++}`;

    @Input() placeholder = 'Enter text...';
    @Input() minHeight = '80px';
    @Input() maxHeight = '400px';
    @Input() title: string;
    @Input() htmlContent: string;
    @Input() maxLength: number;
    @Input() config: AngularEditorConfig;

    textEditorFormGroup = new FormGroup({
        htmlEditorControl: new FormControl('', { nonNullable: true })
    });
    editorConfig: AngularEditorConfig = {};
    disabled = false;
    isFocused = false;

    defaultMessage = `<p></p><ul><li>Direct [Applicant Name Mapkey or &#8220;Signer&#8221;] to visit [URL Mapkey or &#8220;signing portal&#8221;]</li><li>[Applicant Name Mapkey or &#8220;Signer&#8221;] will enter secure code and last four of SSN</li></ul><p></p>`;

    onChange: (value: string) => void = () => {};
    onTouched: () => void = () => {};

    constructor(private sanitizer: DomSanitizer) {
        this.updateEditorConfig();
        this.textEditorFormGroup.controls.htmlEditorControl.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(value => {
                const html = this.sanitize(value);
                if (html !== value) {
                    this.textEditorFormGroup.controls.htmlEditorControl.setValue(html, { emitEvent: false });
                }
                this.onChange(html);
            });
    }

    get characterCount(): number {
        return this.textEditorFormGroup.controls.htmlEditorControl.value.length;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateEditorConfig();
        if (changes.htmlContent) {
            this.writeValue(this.htmlContent || this.defaultMessage);
        }
        if (changes.maxLength) {
            const control = this.textEditorFormGroup.controls.htmlEditorControl;
            control.setValidators(this.maxLength ? Validators.maxLength(this.maxLength) : null);
            control.updateValueAndValidity({ emitEvent: false });
        }
    }

    writeValue(value: string): void {
        this.textEditorFormGroup.controls.htmlEditorControl.setValue(this.sanitize(value), { emitEvent: false });
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.updateEditorConfig();
        const control = this.textEditorFormGroup.controls.htmlEditorControl;
        if (isDisabled) {
            control.disable({ emitEvent: false });
        } else {
            control.enable({ emitEvent: false });
        }
    }

    private sanitize(value: string): string {
        const html = this.sanitizer.sanitize(SecurityContext.HTML, value ?? '') ?? '';
        return /^(?:\s|&nbsp;|<br\s*\/?>|<\/?(?:p|div)>)*$/i.test(html) ? '' : html;
    }

    private updateEditorConfig(): void {
        this.editorConfig = {
            spellcheck: true,
            minHeight: this.minHeight,
            maxHeight: this.maxHeight,
            defaultParagraphSeparator: 'p',
            toolbarHiddenButtons: [[
                'subscript', 'superscript', 'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull',
                'indent', 'outdent', 'heading', 'fontName', 'fontSize', 'textColor', 'backgroundColor',
                'customClasses', 'insertImage', 'insertVideo', 'insertHorizontalRule'
            ]],
            ...this.config,
            editable: !this.disabled && this.config?.editable !== false,
            sanitize: true
        };
    }
}
